import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Alert, Breadcrumb, Button, Card, Col, Container, OverlayTrigger, Row, Spinner, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt, faSync } from "@fortawesome/free-solid-svg-icons";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import Loader from "../../components/Loader.js";
import useLayout from "../../hooks/useLayout.js";

const EMBED_SCRIPT_ID = "trello-embed-script";
const EMBED_SCRIPT_URL = "https://p.trellocdn.com/embed.min.js";
const BOARD_ID = "699bf81fa612a5fa3d684aa3";

const activityText = (activity, cardUrl) => {
  const cardName = activity.card?.name || "um cartão";
  const linkedCard = (
    <>
      “<a className="text-decoration-underline" href={cardUrl} target="_blank" rel="noopener noreferrer">{cardName}</a>”
    </>
  );
  switch (activity.type) {
    case "commentCard":
      return <>comentou em {linkedCard}</>;
    case "createCard":
      return activity.list?.name
        ? <>adicionou {linkedCard} a {activity.list.name}</>
        : <>adicionou {linkedCard} ao quadro</>;
    case "deleteCard":
      return <>excluiu {linkedCard}</>;
    case "addMemberToCard":
      return <>adicionou um membro a {linkedCard}</>;
    case "removeMemberFromCard":
      return <>removeu um membro de {linkedCard}</>;
    case "updateCheckItemStateOnCard":
      return <>atualizou um checklist em {linkedCard}</>;
    case "updateCard":
      if (activity.old?.closed === false && activity.card?.closed === true) {
        return <>arquivou {linkedCard}</>;
      }
      if (activity.old?.closed === true && activity.card?.closed === false) {
        return <>reabriu {linkedCard}</>;
      }
      if (activity.listBefore?.name && activity.listAfter?.name) {
        return <>moveu {linkedCard} de {activity.listBefore.name} para {activity.listAfter.name}</>;
      }
      return <>atualizou {linkedCard}</>;
    case "addAttachmentToCard":
      return <>anexou {activity.attachment?.name || "um arquivo"} a {linkedCard}</>;
    case "deleteAttachmentFromCard":
      return <>removeu {activity.attachment?.name || "um anexo"} de {linkedCard}</>;
    case "addLabelToCard":
      return <>adicionou uma etiqueta a {linkedCard}</>;
    case "removeLabelFromCard":
      return <>removeu uma etiqueta de {linkedCard}</>;
    default:
      return <>registrou uma atividade em {linkedCard}</>;
  }
};

const relativeDate = (value) => {
  if (!value) return "";
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true, locale: ptBR });
  } catch (error) {
    return "";
  }
};

const Trello = () => {
  const embedContainerRef = useRef(null);
  const { setLoading } = useLayout();
  const configuredBoardUrl = (process.env.REACT_APP_TRELLO_BOARD_URL || "").trim();
  const [state, setState] = useState({ loading: true, error: "", board: null, activities: [] });
  const boardContentReady = !state.loading || Boolean(state.board);

  const loadActivity = useCallback(async ({ showGlobalLoading = false } = {}) => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    if (showGlobalLoading) setLoading(true);
    try {
      const response = await axios.get("/api/trello/activity", { params: { limit: 20 } });
      setState({
        loading: false,
        error: "",
        board: response.data?.board || null,
        activities: Array.isArray(response.data?.activities) ? response.data.activities : [],
      });
    } catch (error) {
      setState((current) => ({
        ...current,
        loading: false,
        error: error.response?.data?.message || "Não foi possível carregar as atividades do Trello.",
      }));
    } finally {
      if (showGlobalLoading) setLoading(false);
    }
  }, [setLoading]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  useEffect(() => {
    if (!boardContentReady) return undefined;

    const loadBoard = () => {
      if (window.TrelloBoards && embedContainerRef.current) {
        embedContainerRef.current.replaceChildren();
        window.TrelloBoards.create(configuredBoardUrl || BOARD_ID, embedContainerRef.current);
      }
    };

    const existingScript = document.getElementById(EMBED_SCRIPT_ID);
    if (existingScript) {
      if (window.TrelloBoards) loadBoard();
      else existingScript.addEventListener("load", loadBoard, { once: true });
      return () => existingScript.removeEventListener("load", loadBoard);
    }

    const script = document.createElement("script");
    script.id = EMBED_SCRIPT_ID;
    script.src = EMBED_SCRIPT_URL;
    script.async = true;
    script.addEventListener("load", loadBoard, { once: true });
    document.body.appendChild(script);
    return () => script.removeEventListener("load", loadBoard);
  }, [configuredBoardUrl, boardContentReady]);

  const boardUrl = state.board?.url || configuredBoardUrl || "https://trello.com";

  const hasActivityData = Boolean(state.board) || state.activities.length > 0;

  if (state.loading && !hasActivityData) {
    return <Loader />;
  }

  return (
    <>
      <Button
        type="button"
        variant="primary"
        className="btn update-side-panel-button"
        onClick={() => loadActivity({ showGlobalLoading: true })}
        disabled={state.loading}
        title="Atualizar atividades"
        aria-label="Atualizar atividades"
      >
        <FontAwesomeIcon icon={faSync} spin={state.loading} size="lg" />
      </Button>

      <Container className="p-0 d-flex flex-column container w-100">
      <Row className="mx-0">
        <Col xs="auto" className="order-1 px-0 me-3">
          <Breadcrumb>
            <Breadcrumb.Item active>Trello</Breadcrumb.Item>
          </Breadcrumb>
          <h6 className="text-muted mb-3">Atividades recentes de {state.board?.name || "R1 Eventech - Webapps"}</h6>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Body>
          <h5 className="card-title mb-2">{state.board?.name || "R1 Eventech - Webapps"}</h5>
          {state.board?.sections?.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mb-3">
              {state.board.sections.map((section) => (
                <span
                  key={section.id || section.name}
                  className={`badge ${section.name === "Em Progresso" ? "bg-primary text-dark" : "bg-light text-dark border"}`}
                >
                  {section.name}: {section.totalCards}
                </span>
              ))}
            </div>
          )}
          <div ref={embedContainerRef}>Carregando quadro do Trello...</div>
        </Card.Body>
      </Card>

      <div className="textHeaderAplicativo mb-3 col">
        <h5 className="card-title mb-0">Histórico de atividade</h5>
      </div>

      <div>
          {state.loading && state.activities.length === 0 && (
            <div className="text-center text-muted py-5">
              <Spinner animation="border" size="sm" className="me-2" />
              Carregando atividades...
            </div>
          )}

          {!state.loading && state.error && (
            <Alert variant="danger" className="p-2 mb-3">
              {state.error}
            </Alert>
          )}

          {!state.loading && !state.error && state.activities.length === 0 && (
            <div className="text-center text-muted py-5">Nenhuma atividade recente encontrada.</div>
          )}

          {state.activities.length > 0 && (
            <div className="list-group">
              {state.activities.map((activity) => {
                const memberName = activity.member?.fullName || "Usuário do Trello";
                const cardUrl = activity.card?.shortLink
                  ? `https://trello.com/c/${activity.card.shortLink}`
                  : boardUrl;
                return (
                  <div key={activity.id} className="list-group-item list-group-item-action mb-2" aria-current="true">
                    <div className="d-flex w-100">
                      {activity.member?.avatarUrl ? (
                        <img src={`${activity.member.avatarUrl}/50.png`} alt="" width="48" height="48" className="rounded me-3 flex-shrink-0" />
                      ) : (
                        <div className="rounded bg-primary text-white d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: 48, height: 48 }} aria-hidden="true">
                          {memberName.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-grow-1">
                        <div className="d-flex w-100 justify-content-between align-items-start gap-3">
                          <div>
                            <h5 className="mb-0 me-2">{memberName}</h5>
                            <div className="text-muted mt-1">{activityText(activity, cardUrl)}</div>
                          </div>
                        </div>
                        <div className="d-flex w-100 justify-content-between gap-3 mt-2">
                          <div>
                            {activity.comment && <div className="text-muted">“{activity.comment}”</div>}
                            <small className="text-muted">{relativeDate(activity.date)}</small>
                          </div>
                          <div className="d-flex gap-2 flex-shrink-0">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Abrir no Trello</Tooltip>}>
                              <span className="d-inline-block">
                                <a className="btn btn-sm btn-primary" href={cardUrl} target="_blank" rel="noopener noreferrer" aria-label="Abrir atividade no Trello">
                                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                                </a>
                              </span>
                            </OverlayTrigger>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
      </Container>
    </>
  );
};

export default Trello;
